<template>
  <x-form
    modal
    :open="open"
    :title="$t('Nuovo registrante')"
    variant="medium"
    :submit-text="$t('Salva')"
    :submit-help="$t('* Campo obbligatorio')"
    ouia-id="domain-contact-form"
    @update:open="$emit('update:open', $event)"
    @submit="submit"
  >
    <form-group :name="$t('Tipologia')" required>
      <pf-form-select
        id="domainContactEntityType"
        :model-value="contact.entity_type"
        name="entity_type"
        required
        @update:model-value="contact.entity_type = $as<DomainContactCreate['entity_type']>($event)"
      >
        <pf-form-select-option v-for="type in entityTypes" :key="type" :value="type">
          {{ entityTypeLabels[type] }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="$t('Nome e cognome')" required>
      <x-input id="domainContactName" v-model="contact.name" name="name" required maxlength="255" autocomplete="name" />
    </form-group>

    <form-group v-if="!individual" :name="$t('Ragione Sociale')" required>
      <x-input id="domainContactCompany" v-model="contact.company" name="company" required maxlength="255" autocomplete="organization" />
    </form-group>

    <form-group :name="$t('Email')" required>
      <x-input id="domainContactEmail" v-model="contact.email" name="email" type="email" required maxlength="255" autocomplete="email" />
    </form-group>

    <form-group :name="$t('Nazione')" required>
      <pf-form-select
        id="domainContactCountry"
        :model-value="contact.country"
        name="country"
        autocomplete="country"
        required
        @update:model-value="contact.country = String($event)"
      >
        <pf-form-select-option v-for="country in countries" :key="country.iso" :value="country.iso">
          {{ country.country }}
        </pf-form-select-option>
      </pf-form-select>
    </form-group>

    <form-group :name="individual ? $t('Indirizzo residenza') : $t('Indirizzo sede')" required>
      <pf-textarea id="domainContactAddress" v-model="contact.address" name="address" required rows="2" maxlength="255" autocomplete="street-address" />
    </form-group>

    <form-group :name="$t('CAP')" required>
      <x-input id="domainContactZipcode" v-model="contact.zipcode" name="zipcode" required maxlength="16" autocomplete="postal-code" />
    </form-group>

    <form-group :name="$t('Città')" required>
      <x-input id="domainContactCity" v-model="contact.city" name="city" required maxlength="255" />
    </form-group>

    <form-group :name="contact.country == 'CH' ? $t('Cantone') : $t('Provincia')" required>
      <x-input id="domainContactProvince" v-model="contact.province" name="province" required maxlength="255" />
    </form-group>

    <form-group :name="individual ? $t('Codice fiscale') : $t('Partita IVA / Codice fiscale')" :required="regCodeRequired">
      <x-input id="domainContactRegCode" v-model="contact.reg_code" name="reg_code" :required="regCodeRequired" maxlength="36" />
    </form-group>

    <form-group v-if="individual" :name="$t('Acconsento alla pubblicazione dei dati personali nel database WHOIS')" no-label>
      <pf-checkbox
        id="domainContactConsent"
        v-model="contact.consent_for_publishing"
        name="consent_for_publishing"
        :label="$t('Acconsento alla pubblicazione dei dati personali nel database WHOIS')"
      />
    </form-group>
  </x-form>
</template>

<script lang="ts">
import { $t as _t } from '@/i18n';
import type { DomainContactEntityType } from '@/resources';

export const entityTypeLabels: Record<DomainContactEntityType, string> = {
  Individual: _t('Persona fisica'),
  Company: _t('Azienda'),
  Freelance: _t('Libero professionista'),
  NoProfit: _t('Ente no profit'),
  Public: _t('Ente pubblico'),
  Other: _t('Altro'),
  Foreign: _t('Soggetto estero'),
};
</script>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { DomainContactResource, type DomainContactCreate } from '@/resources';
import countries from '../countries.json';

withDefaults(defineProps<{ open?: boolean }>(), { open: true });

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'saved', id: number): void;
}>();

const entityTypes: DomainContactCreate['entity_type'][] = ['Individual', 'Company', 'Freelance', 'NoProfit', 'Public', 'Other'];

const contact = ref<DomainContactCreate>({
  entity_type: 'Individual',
  name: '',
  email: '',
  company: null,
  country: 'IT',
  zipcode: '',
  address: '',
  city: '',
  province: '',
  reg_code: null,
  consent_for_publishing: false,
});

const individual = computed(() => contact.value.entity_type === 'Individual');
const regCodeRequired = computed(() => contact.value.country === 'IT');

async function submit() {
  const { id } = await new DomainContactResource().save({
    ...contact.value,
    company: individual.value ? null : contact.value.company,
    consent_for_publishing: individual.value ? contact.value.consent_for_publishing : true,
  });
  emit('saved', id);
  emit('update:open', false);
}
</script>
